import { createContext, useContext, useState, useCallback, useRef } from 'react';
import '../styles/Toast.css';

const ToastContext = createContext(null);

/**
 * Usage anywhere inside the app:
 *
 *   const { addToast } = useToast();
 *   addToast({ title, body, cta: { label, onClick } });
 *
 * Toasts auto-dismiss after 6 seconds. cta is optional.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, leaving: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timerRefs.current[id];
    }, 280); // matches CSS leave animation
  }, []);

  const addToast = useCallback(({ title, body, cta, duration = 6000 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [{ id, title, body, cta, leaving: false }, ...prev].slice(0, 5));
    timerRefs.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ addToast, dismiss }}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`toast-item${t.leaving ? ' toast-leaving' : ''}`}
          >
            <div className="toast-body">
              {t.title && <div className="toast-title">{t.title}</div>}
              {t.body  && <div className="toast-text">{t.body}</div>}
              {t.cta && (
                <button
                  className="toast-cta"
                  onClick={() => { t.cta.onClick(); dismiss(t.id); }}
                >
                  {t.cta.label}
                </button>
              )}
            </div>
            <button className="toast-close" onClick={() => dismiss(t.id)}>✕</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
